import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../utils/apiClient';
import { Robot, RobotStatus, RobotCreate } from '../../types';

interface RobotsState {
  robots: Robot[];
  allRobots: Robot[];
  selectedRobot: Robot | null;
  robotStatus: RobotStatus | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: RobotsState = {
  robots: [],
  allRobots: [],
  selectedRobot: null,
  robotStatus: null,
  isLoading: false,
  error: null,
};

export const fetchMyRobots = createAsyncThunk<Robot[], void, { rejectValue: string }>(
  'robots/fetchMyRobots',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<Robot[]>('/robots');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch robots');
    }
  }
);

export const fetchAllRobots = createAsyncThunk<Robot[], void, { rejectValue: string }>(
  'robots/fetchAllRobots',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<Robot[]>('/admin/robots');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch robots');
    }
  }
);

export const registerRobot = createAsyncThunk<Robot, RobotCreate, { rejectValue: string }>(
  'robots/registerRobot',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<Robot>('/admin/robots', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to register robot');
    }
  }
);

export const deleteRobot = createAsyncThunk<string, string, { rejectValue: string }>(
  'robots/deleteRobot',
  async (robotId, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/robots/${robotId}`);
      return robotId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete robot');
    }
  }
);

export const assignRobot = createAsyncThunk<Robot, { robotId: string; userId: string }, { rejectValue: string }>(
  'robots/assignRobot',
  async ({ robotId, userId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<Robot>(`/admin/robots/${robotId}/assign`, { user_id: userId });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to assign robot');
    }
  }
);

export const fetchRobotStatus = createAsyncThunk<RobotStatus, string, { rejectValue: string }>(
  'robots/fetchRobotStatus',
  async (robotId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<RobotStatus>(`/robots/${robotId}/status`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch status');
    }
  }
);

const robotsSlice = createSlice({
  name: 'robots',
  initialState,
  reducers: {
    setSelectedRobot: (state, action: PayloadAction<Robot | null>) => { state.selectedRobot = action.payload; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyRobots.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchMyRobots.fulfilled, (state, action) => { state.isLoading = false; state.robots = action.payload; })
      .addCase(fetchMyRobots.rejected, (state, action) => { state.isLoading = false; state.error = action.payload || 'Failed'; })
      .addCase(fetchAllRobots.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchAllRobots.fulfilled, (state, action) => { state.isLoading = false; state.allRobots = action.payload; })
      .addCase(fetchAllRobots.rejected, (state, action) => { state.isLoading = false; state.error = action.payload || 'Failed'; })
      .addCase(registerRobot.fulfilled, (state, action) => { state.allRobots.unshift(action.payload); })
      .addCase(deleteRobot.fulfilled, (state, action) => { state.allRobots = state.allRobots.filter((r) => r.id !== action.payload); })
      .addCase(fetchRobotStatus.fulfilled, (state, action) => { state.robotStatus = action.payload; });
  },
});

export const { setSelectedRobot, clearError } = robotsSlice.actions;
export default robotsSlice.reducer;
